import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { MAJOR_ARCANA, SUIT_INFO } from "@/data/tarotDeck";
import GuidanceCard from "@/components/GuidanceCard";

const CardTile = ({ card, onClick, index }) => {
  const [errored, setErrored] = useState(false);
  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.02, 0.4) }}
      onClick={() => onClick(card)}
      className="deck-card-light group relative flex flex-col text-left focus:outline-none"
      data-testid={`deck-card-${card.id}`}
      aria-label={`${card.name} — click to view details`}
    >
      <div className="aspect-[5/8] relative" style={{ background: "#F1F4FA" }}>
        {!errored ? (
          <img
            src={card.imageUrl}
            alt={card.name}
            loading="lazy"
            onError={() => setErrored(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-center p-3"
               style={{ background: "linear-gradient(135deg,#25478e,#131a48)", color: "#fff", fontFamily: "'Cormorant Garamond', serif" }}>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">
                {card.arcana === "major" ? "Major" : card.suit}
              </div>
              <div className="text-base italic">{card.name}</div>
            </div>
          </div>
        )}
      </div>
      <div className="p-2.5 text-center bg-white">
        <div className="text-[10px] uppercase tracking-widest" style={{ color: "#64748B" }}>
          {card.arcana === "major" ? `MAJOR · ${card.label}` : `${String(card.n).padStart(2, "0")}`}
        </div>
        <div className="font-serif-display text-sm leading-tight mt-0.5" style={{ color: "var(--cs-navy)" }}>
          {card.name}
        </div>
      </div>
    </motion.button>
  );
};

const DeckSection = ({ onCardClick }) => {
  return (
    <section id="deck" className="relative section section-light" data-testid="deck-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--cs-purple)" }}>The Complete Deck</p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-5xl md:text-6xl leading-tight"
            style={{ color: "var(--cs-blue-dk)" }}
            data-testid="deck-heading"
          >
            The 78 Cards of the <span className="italic" style={{ color: "var(--cs-purple)" }}>Tarot Deck</span>
          </motion.h2>
          <p className="mt-5 text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "#475569" }}>
            Explore the full tarot deck — each card a doorway to deeper understanding. Click any card to reveal its complete meaning.
          </p>
        </div>

        {/* Fool's Journey */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl p-8 md:p-10 max-w-4xl mx-auto mb-12"
          style={{ background: "#F1F4FA", border: "1px solid rgba(19,26,72,0.08)" }}
          data-testid="fools-journey-box"
        >
          <h3 className="font-serif-display text-2xl md:text-3xl mb-4" style={{ color: "var(--cs-blue-dk)" }}>The Fool's Journey</h3>
          <p className="leading-relaxed" style={{ color: "#334155" }}>
            The tarot depicts the fool's (consciousness) journey from ignorance to mastery. Each card represents a significant stage on the universal journey to self-discovery — known as the Fool's or hero's journey. The Fool encounters the collective archetypes as he moves through the cards, gathering wisdom, weathering trials, and ultimately becoming whole.
          </p>
        </motion.div>

        {/* MAJOR ARCANA */}
        <div className="mb-16">
          <div className="flex items-baseline gap-4 mb-6">
            <h3 className="font-serif-display text-3xl" style={{ color: "var(--cs-navy)" }}>Major Arcana</h3>
            <span className="text-xs uppercase tracking-widest" style={{ color: "#64748B" }}>The Fool to The World · 22 cards</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 md:gap-4">
            {MAJOR_ARCANA.map((c, i) => (
              <CardTile key={c.id} card={c} onClick={onCardClick} index={i} />
            ))}
          </div>
        </div>

        {/* SUITS */}
        {SUIT_INFO.map((suit) => (
          <div key={suit.key} className="mb-16" data-testid={`suit-section-${suit.key}`}>
            <div className="flex items-baseline gap-4 mb-6 flex-wrap">
              <h3 className="font-serif-display text-3xl" style={{ color: "var(--cs-navy)" }}>{suit.label}</h3>
              <span className="text-xs uppercase tracking-widest" style={{ color: "#64748B" }}>
                {suit.element} · {suit.theme} · 14 cards
              </span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-3 md:gap-4">
              {suit.cards.map((c, i) => (
                <CardTile key={c.id} card={c} onClick={onCardClick} index={i} />
              ))}
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="text-center mt-12 mb-14">
          <p className="mb-5 italic font-serif-display text-lg" style={{ color: "#475569" }}>
            Want a personal reading with these cards?
          </p>
          <button onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} className="btn-pill-primary" data-testid="deck-cta-book">
            <Sparkles className="w-4 h-4" /> Book a reading with Verna
          </button>
        </div>

        {/* Interactive: What is your guidance for today */}
        <div className="pt-10 border-t" style={{ borderColor: "rgba(19,26,72,0.08)" }}>
          <GuidanceCard />
        </div>
      </div>
    </section>
  );
};

export default DeckSection;
